/**
 * 商品メディアギャラリー安定表示スクリプト
 * 問題: 複数のlazy loading実装が競合し、2枚目以降の画像が一瞬表示後に消える
 * 解決: 商品ページ専用の統一された画像表示制御
 * 作成日: 2025-09-14
 */

(function() {
  'use strict';
  
  // 商品ページでのみ実行
  if (!document.body.classList.contains('template-product')) {
    return;
  }
  
  console.log('[Product Media Gallery] 安定表示モード初期化開始');
  
  // 設定
  const config = {
    rootMargin: '50px 0px',
    threshold: 0.01,
    transitionDuration: 300
  };
  
  // 初期化関数
  function initStableGallery() {
    // 商品ギャラリー内のすべての画像を取得
    const galleryImages = document.querySelectorAll(
      '.product__media img, ' +
      '.product__media-item img, ' +
      '.media img, ' +
      '.product-media-container img'
    );
    
    if (galleryImages.length === 0) {
      console.warn('[Product Media Gallery] 画像が見つかりません');
      return;
    }
    
    console.log(`[Product Media Gallery] ${galleryImages.length}枚の画像を検出`);
    
    // 既存のlazy loading設定を無効化
    galleryImages.forEach((img, index) => {
      // ネイティブlazy loadingを無効化
      img.loading = 'eager';
      
      // 既存のlazy loadクラスを削除
      img.classList.remove('lazy', 'lazyload', 'lazy-loading');
      
      // data属性を実際のsrc/srcsetに設定
      if (img.dataset.src && !img.src.includes(img.dataset.src)) {
        img.setAttribute('data-original-src', img.dataset.src);
      }
      if (img.dataset.srcset && !img.srcset) {
        img.setAttribute('data-original-srcset', img.dataset.srcset);
      }
      
      // opacity強制設定（CSS競合対策）
      img.style.opacity = '0';
      img.style.transition = `opacity ${config.transitionDuration}ms ease-in-out`;
      
      // overflow: clip設定（Chrome 108+対応）
      img.style.overflow = 'clip';
      
      // 画像インデックスを設定
      img.setAttribute('data-gallery-index', index);
    });
    
    // 安定した画像表示関数
    function showImage(img) {
      if (img.classList.contains('stable-loaded')) {
        return;
      }
      
      const originalSrc = img.getAttribute('data-original-src');
      const originalSrcset = img.getAttribute('data-original-srcset');
      
      // srcとsrcsetを設定
      if (originalSrc && !img.src.includes(originalSrc)) {
        img.src = originalSrc;
      }
      if (originalSrcset && !img.srcset) {
        img.srcset = originalSrcset;
      }
      
      // 画像読み込み完了時の処理
      const onLoadHandler = function() {
        img.style.opacity = '1';
        img.classList.add('stable-loaded');
        console.log(`[Product Media Gallery] 画像${img.getAttribute('data-gallery-index')}表示完了`);
      };
      
      // 既に読み込み済みの場合
      if (img.complete && img.naturalHeight !== 0) {
        onLoadHandler();
      } else {
        img.addEventListener('load', onLoadHandler, { once: true });
        
        // エラー時の処理
        img.addEventListener('error', function() {
          console.error(`[Product Media Gallery] 画像${img.getAttribute('data-gallery-index')}読み込みエラー`);
          img.style.opacity = '0.3';
        }, { once: true });
      }
    }
    
    // 1枚目の画像は即座に表示
    if (galleryImages[0]) {
      showImage(galleryImages[0]);
    }
    
    // 2枚目以降はIntersection Observerで制御
    if (galleryImages.length > 1) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            showImage(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, config);
      
      // 2枚目以降を監視対象に追加
      for (let i = 1; i < galleryImages.length; i++) {
        observer.observe(galleryImages[i]);
      }
    }
    
    // グローバルなlazy loading処理を無効化
    disableGlobalLazyLoad();
  }
  
  // グローバルlazy loading無効化
  function disableGlobalLazyLoad() {
    // theme.liquidのIntersection Observerを商品ページで無効化
    const globalObservers = window.imageObserver || window.lazyImageObserver;
    if (globalObservers && typeof globalObservers.disconnect === 'function') {
      globalObservers.disconnect();
      console.log('[Product Media Gallery] グローバルlazy loading無効化');
    }
    
    // 商品ページの画像に対するグローバルスタイルを上書き
    const style = document.createElement('style');
    style.textContent = `
      .template-product .product__media img,
      .template-product .product__media-item img,
      .template-product .media img {
        opacity: 0 !important;
        transition: opacity ${config.transitionDuration}ms ease-in-out !important;
      }
      
      .template-product .product__media img.stable-loaded,
      .template-product .product__media-item img.stable-loaded,
      .template-product .media img.stable-loaded {
        opacity: 1 !important;
        visibility: visible !important;
        display: block !important;
      }
      
      /* Chrome 108+ overflow対策 */
      .template-product img {
        overflow: clip !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  // メディアギャラリーの切り替えイベントを監視
  function observeGalleryChanges() {
    // 商品バリアント変更時の処理
    document.addEventListener('variant:change', function() {
      console.log('[Product Media Gallery] バリアント変更検出');
      setTimeout(initStableGallery, 100);
    });
    
    // サムネイルクリック時の処理
    const thumbnails = document.querySelectorAll('.product__media-list button, .thumbnail-list__item button');
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', function() {
        console.log('[Product Media Gallery] サムネイルクリック検出');
        setTimeout(initStableGallery, 100);
      });
    });
    
    // DOM変更を監視（動的コンテンツ対応）
    const galleryContainer = document.querySelector('.product__media-wrapper, .product-media-container');
    if (galleryContainer) {
      const mutationObserver = new MutationObserver(function(mutations) {
        const hasImageChanges = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => 
            node.nodeName === 'IMG' || (node.querySelector && node.querySelector('img'))
          );
        });
        
        if (hasImageChanges) {
          console.log('[Product Media Gallery] DOM変更検出');
          initStableGallery();
        }
      });
      
      mutationObserver.observe(galleryContainer, {
        childList: true,
        subtree: true
      });
    }
  }
  
  // 初期化実行
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initStableGallery();
      observeGalleryChanges();
    });
  } else {
    // 既にDOMContentLoadedが発火済みの場合
    initStableGallery();
    observeGalleryChanges();
  }
  
  // ページ完全読み込み後の再チェック
  window.addEventListener('load', function() {
    setTimeout(function() {
      const unstableImages = document.querySelectorAll(
        '.product__media img:not(.stable-loaded), ' +
        '.product__media-item img:not(.stable-loaded)'
      );
      
      if (unstableImages.length > 0) {
        console.log(`[Product Media Gallery] 未表示画像${unstableImages.length}枚を再処理`);
        initStableGallery();
      }
    }, 500);
  });
  
  console.log('[Product Media Gallery] 安定表示モード初期化完了');
})();